import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CurrentViewPointerType } from "../../components/unboarding/CurrentViewPointerType";
import { UnboardingCurrentViewPointer } from "../../components/unboarding/UnboardingCurrentViewPointer";
import { UnboardingType } from "../../components/unboarding/UnboardingType";
import { UnboardingResourcePackage } from "../../resources/UnboardingResourcePackage";
import { AdditionalRenterPage } from "./pages/AdditionalRenterPage";
import { FirstPage } from "./pages/FirstPage";
import { SecondPage } from "./pages/SecondPage";
import { ThirdPage } from "./pages/ThirdPage";
import { IFirstTimeExperienceModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IFirstTimeExperienceModel";

export class UnboardingPopupView extends BaseScalableView<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly unboardingResources: UnboardingResourcePackage;
    private readonly myBuildingsDynamicResources: DynamicResourcesPackage;
    private readonly playerModel: IPlayerModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly type: UnboardingType;

    private viewBackground: Sprite;
    private topLight: Sprite;
    private bottomLight: Sprite;
    private currentViewPointer: UnboardingCurrentViewPointer;
    private topText: Text;

    private firstPage: FirstPage;
    private firstPagePCS: PromiseCompletionSource<void>;
    private secondPage: SecondPage;
    private secondPagePCS: PromiseCompletionSource<void>;
    private additionalRenterPage: AdditionalRenterPage;
    private additionalRenterPagePCS: PromiseCompletionSource<void>;
    private thirdPage: ThirdPage;
    private thirdPagePCS: PromiseCompletionSource<void>;
    private skipButton: ButtonWithScaleAnimation;
    private mainFlowAnimation: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        unboardingResources: UnboardingResourcePackage,
        myBuildingsDynamicResources: DynamicResourcesPackage,
        playerModel: IPlayerModel,
        firstTimeExperienceModel: IFirstTimeExperienceModel,
        type: UnboardingType
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.unboardingResources = unboardingResources;
        this.myBuildingsDynamicResources = myBuildingsDynamicResources;
        this.playerModel = playerModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.type = type;
        this.firstPagePCS = new PromiseCompletionSource<void>();
        this.secondPagePCS = new PromiseCompletionSource<void>();
        this.additionalRenterPagePCS = new PromiseCompletionSource<void>();
        this.thirdPagePCS = new PromiseCompletionSource<void>();
        this.mainFlowAnimation = new gsap.core.Timeline();
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        this.firstPage.visible = false;
        this.secondPage.visible = false;
        this.additionalRenterPage.visible = false;
        this.thirdPage.visible = false;
        this.currentViewPointer.visible = false;
        this.topText.visible = false;
        this.skipButton.visible = false;
        this.show();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = 'static';
        this.addChild(this.viewBackground);
        switch (this.type) {
            case UnboardingType.OWNER:
                this.topLight = new Sprite(this.unboardingResources.assets_views_unboarding_top_light_green_png);
                this.addChild(this.topLight);
                break;
            case UnboardingType.RENTER:
                this.topLight = new Sprite(this.unboardingResources.assets_views_unboarding_top_light_purpule_png);
                this.addChild(this.topLight);
                break;
        }
        this.bottomLight = new Sprite(this.unboardingResources.assets_views_unboarding_bottom_light_png);
        this.bottomLight.pivot.y = this.bottomLight.height;
        this.addChild(this.bottomLight);


        this.firstPage = new FirstPage(
            this.firstPagePCS,
            this.commonResources,
            this.unboardingResources,
            this.myBuildingsDynamicResources,
            this.canvasScaler,
            this.playerModel,
            this.type
        );
        this.addChild(this.firstPage);
        this.secondPage = new SecondPage(
            this.secondPagePCS,
            this.commonResources,
            this.unboardingResources,
            this.canvasScaler,
            this.type
        );
        this.addChild(this.secondPage);
        this.additionalRenterPage = new AdditionalRenterPage(
            this.additionalRenterPagePCS,
            this.commonResources,
            this.unboardingResources,
            this.canvasScaler,
            this.type
        );
        this.addChild(this.additionalRenterPage);
        this.thirdPage = new ThirdPage(
            this.thirdPagePCS,
            this.commonResources,
            this.unboardingResources,
            this.canvasScaler,
            this.type
        );
        this.addChild(this.thirdPage);

        this.currentViewPointer = new UnboardingCurrentViewPointer(this.unboardingResources);
        this.addChild(this.currentViewPointer);
        this.topText = new Text(`MOVE UP THE CITY VERSE LADDER`, new TopTextStyle());
        this.addChild(this.topText);
        this.skipButton = new ButtonWithScaleAnimation(this.unboardingResources.assets_views_unboarding_skip_button_png);
        this.skipButton.onUp.add(this.onSkipButtonClickHandle, this);
        this.addChild(this.skipButton);
    }

    private async show(): Promise<void> {
        this.mainFlowAnimation.fromTo(this.firstPage, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.firstPage.visible = true });
        this.mainFlowAnimation.fromTo(this.skipButton, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.skipButton.visible = (this.type === UnboardingType.RENTER && !this.firstTimeExperienceModel.becameRenter.value) }, "<");
        await this.firstPagePCS.result;
        this.mainFlowAnimation.fromTo(this.firstPage, { alpha: 1 }, {
            alpha: 0, duration: 0.7, onComplete: () => {
                this.removeChild(this.firstPage);
            }
        });
        await this.mainFlowAnimation.play();
        this.mainFlowAnimation.clear();
        this.mainFlowAnimation.fromTo(this.currentViewPointer, { alpha: 0 }, {
            alpha: 1, duration: 0.7, onStart: () => {
                this.currentViewPointer.visible = true;
                this.currentViewPointer.showSelectedView(this.type === UnboardingType.RENTER ? CurrentViewPointerType.FIRST : CurrentViewPointerType.SECOND);
            }
        });
        this.mainFlowAnimation.fromTo(this.topText, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.topText.visible = true }, "<");
        this.mainFlowAnimation.fromTo(this.secondPage, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.secondPage.visible = true }, "<");
        await this.secondPagePCS.result;
        this.mainFlowAnimation.fromTo(this.secondPage, { alpha: 1 }, {
            alpha: 0, duration: 0.7, onComplete: () => {
                this.removeChild(this.secondPage);
            }
        })
        await this.mainFlowAnimation.play();
        this.mainFlowAnimation.clear();
        if (this.type === UnboardingType.RENTER) {
            this.mainFlowAnimation.fromTo(this.additionalRenterPage, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.additionalRenterPage.visible = true });
            await this.additionalRenterPagePCS.result;
            this.mainFlowAnimation.fromTo(this.additionalRenterPage, { alpha: 1 }, {
                alpha: 0, duration: 0.7, onComplete: () => {
                    this.removeChild(this.additionalRenterPage);
                }
            })
            await this.mainFlowAnimation.play();
            this.mainFlowAnimation.clear();
        }
        this.currentViewPointer.showSelectedView(this.type === UnboardingType.RENTER ? CurrentViewPointerType.SECOND : CurrentViewPointerType.THIRD);
        this.skipButton.visible = false;
        this.mainFlowAnimation.fromTo(this.thirdPage, { alpha: 0 }, { alpha: 1, duration: 0.7, onStart: () => this.thirdPage.visible = true });
        await this.thirdPagePCS.result;
        this.mainFlowAnimation.fromTo(this.thirdPage, { alpha: 1 }, {
            alpha: 0, duration: 0.7, onComplete: () => {
                this.removeChild(this.thirdPage);
            }
        })
        await this.mainFlowAnimation.play();
        this.mainFlowAnimation.clear();
        this.mainFlowAnimation.fromTo(this.currentViewPointer, { alpha: 1 }, { alpha: 0, duration: 0.7 });
        this.mainFlowAnimation.fromTo(this.topText, { alpha: 1 }, { alpha: 0, duration: 0.7 }, "<");
        this.mainFlowAnimation.fromTo(this, { alpha: 1 }, { alpha: 0, duration: 0.3 }, "<90%");
        await this.mainFlowAnimation.play();
        this.operationControls.complete();
    }

    private async onSkipButtonClickHandle(): Promise<void> {
        this.skipButton.interactive = false;
        this.mainFlowAnimation.clear();
        this.mainFlowAnimation.fromTo(this, { alpha: 1 }, { alpha: 0, duration: 0.7, onComplete: () => this.operationControls.complete() });
    }

    private layout(_width: number, height: number): void {
        this.bottomLight.position.set(0, height);
        this.currentViewPointer.position.set(40, 181);
        this.topText.position.set(235, 108);
        this.skipButton.position.set(692, 95);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.skipButton.onUp.remove(this.onSkipButtonClickHandle, this);
        this.mainFlowAnimation.kill();
        super.destroy(options);
    }
}

class TopTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            fill: 'ffffff'
        });
    }
}
